import { ReactNode, useEffect, useCallback, useState, useMemo } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useParams } from 'react-router-dom';
import { ProjectContext } from '../contexts/ProjectContext';
import { useFlowActions } from '../contexts/FlowActionsContext';
import { SaveProjectDialog } from '../components/dialogs/SaveProjectDialog';
import ConfirmDialog from '../components/dialogs/ConfirmDialog'; // Import ConfirmDialog
import { Project, CustomNode, CustomEdge } from '../types';
import { projectService } from '../services/project.service';
import { templateService } from '../services/template.service';
import useStore from '../store/useStore';
import { saveProjectId, loadProjectId } from '../store/persistence';
import { reportService } from '../services/report.service';


interface ProjectProviderProps {
    children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
    const [project, setProjectState] = useState<Project | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isInitialBackendCheck, setIsInitialBackendCheck] = useState(true);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false); // State for confirm dialog
    const { showNotification } = useNotifications();
    const [isInitialized, setIsInitialized] = useState(false);
    const location = useLocation();
    const { fitView } = useFlowActions();
    
    // Get auth state once at the component level
    const { isLoading: authLoading, isAuthChecked, token, user } = useAuth();

    const loadProject = useCallback(
        async (projectId: string, isPreview?: boolean) => {
            setIsLoading(true);
            try {
                const loadedProject = await projectService.loadProject(projectId, isPreview);

                if (!loadedProject && isPreview) {
                    setProjectState(null);
                    return;
                } else if (!loadedProject) {
                    throw new Error('Failed to load project');
                }

                setProjectState(loadedProject);
                useStore.getState().setProject(loadedProject);
                
                // Clear report settings from localStorage when opening a project
                reportService.clearReportFromLocalStorage();
                
                // Save the project ID to localStorage for persistence across page refreshes
                // Don't save preview projects
                if (!isPreview && loadedProject.id !== 'unsaved-project') {
                    saveProjectId(loadedProject.id);
                }
                
                showNotification(
                    'success',
                    isPreview ? 'Project loaded in preview mode.' : 'Project loaded successfully'
                );
            } catch (err) {
                console.error('ProjectProvider: Failed to load project', err);
                const error = err as Error;
                let specificNotificationShown = false;

                // Handle specific error cases first and show their notifications
                if (error.message.includes('404')) {
                    setError(new Error('Project not found. It may have been deleted.'));
                    showNotification('error', 'Project not found. It may have been deleted.');
                    specificNotificationShown = true;
                } else if (isPreview) {
                    // Preview mode specific error
                    setError(new Error('This shared project link is invalid or has expired'));
                    showNotification('error', 'This shared project link is invalid or has expired');
                    specificNotificationShown = true;
                } else {
                    // Generic error, set the error state
                    setError(error);
                }
                
                // If it's a generic error AND it's the initial backend check, don't show the "Failed to load project" notification.
                if (!specificNotificationShown && isInitialBackendCheck) {
                    // Suppress the generic "Failed to load project" notification
                    // The error state is already set above.
                } else if (!specificNotificationShown) {
                    // It's a generic error, but not the initial check, so show the notification.
                    showNotification('error', 'Failed to load project');
                }
                
                // Clear the project state
                setProjectState(null);
                useStore.getState().setProject(null);
                
                // Re-throw the error so the calling component can handle it
                throw error;
            } finally {
                setIsLoading(false);
                if (isInitialBackendCheck) {
                    setIsInitialBackendCheck(false);
                }
            }
        },
        [showNotification, isInitialBackendCheck, setIsInitialBackendCheck]
    );

    const isFirstSave = useMemo(() => project?.id === 'unsaved-project', [project?.id]);

    const handleSaveWithDetails = useCallback(
        async (name: string, description: string) => {
            if (!project || !user) return;
            setIsLoading(true);
            try {
                // Get current store state
                const storeState = useStore.getState();

                // Deselect nodes and edges before processing for saving
                const deselectedNodes = storeState.nodes.map(node => ({ ...node, selected: false }));
                const deselectedEdges = storeState.edges.map(edge => ({ ...edge, selected: false }));

                // Process nodes and edges before saving (using deselected versions)
                const nodesToSave = deselectedNodes.map((node) => ({
                    ...node,
                    data: {
                        ...node.data,
                        onClose: undefined,
                        onDelete: undefined,
                        onUpdate: undefined,
                    },
                }));

                // Process edges to ensure all relevant properties are saved (using deselected versions)
                const edgesToSave = deselectedEdges.map((edge) => {
                    // Create a copy to modify
                    let edgeToSave = { ...edge };
                    const edgeData = edgeToSave.data || {};
                    const edgeLabel = edgeData.label || edgeToSave.label || '';

                    // Always store label in data.label for all edge types
                    // This ensures SmoothStepEdgeWithLabel, StepEdgeWithLabel, and StraightEdgeWithLabel
                    // can access the label from data.label
                    edgeToSave.data = { ...edgeData, label: edgeLabel };
                    
                    // Keep top-level label for backward compatibility
                    // but all edge components should read from data.label
                    edgeToSave.label = edgeLabel;

                    // Return the modified edge object, ensuring defaults for core properties
                    return {
                        ...edgeToSave, // Spread the potentially modified edge
                        id: edgeToSave.id,
                        source: edgeToSave.source,
                        target: edgeToSave.target,
                        sourceHandle: edge.sourceHandle || null,
                        targetHandle: edge.targetHandle || null,
                        type: edge.type || 'editable-edge', // Default to 'editable-edge'
                        animated: edge.animated || false,
                        // Ensure style, markers, and data are included if they exist
                        style: edge.style,
                        markerStart: edge.markerStart,
                        markerEnd: edge.markerEnd,
                        data: edgeToSave.data, // Use the updated data with label
                    };
                });

                // Create project with processed nodes and edges
                const projectToSave = new Project({
                    ...project,
                    name,
                    description,
                    nodes: nodesToSave,
                    edges: edgesToSave,
                });

                const savedProject = await projectService.saveProject(projectToSave, user.id);

                // Create new project instance with saved data and current nodes/edges
                const updatedProject = new Project({
                    ...savedProject,
                    nodes: storeState.nodes,
                    edges: storeState.edges,
                });

                // Update both states with the processed project
                setProjectState(updatedProject);
                useStore.getState().setProject(updatedProject);
                showNotification(
                    'success',
                    isFirstSave ? 'Project saved successfully' : 'Changes saved successfully'
                );

            } catch (err) {
                setError(err as Error);
                showNotification('error', 'Failed to save project');
            } finally {
                setIsLoading(false);
            }
        },
        [project, user, showNotification, isFirstSave]
    );

    const saveProject = useCallback(
        async () => {
            if (!project || !user) return;

            if (isFirstSave) {
                setShowSaveDialog(true); // Show name/desc dialog for first save
                return;
            }

            // Check if user is the owner for subsequent saves
            const isOwner = project.owner_id === user.id;

            if (!isOwner) {
                // If not the owner, show confirmation dialog
                setShowSaveConfirmDialog(true);
            } else {
                // If owner, save directly
                await handleSaveWithDetails(project.name, project.description || '');
            }
        },
        [project, user, isFirstSave, handleSaveWithDetails]
    );

    const handleSaveDialogClose = useCallback(() => {
        setShowSaveDialog(false);
    }, []);

    const updateProjectNodes = useCallback((nodes: CustomNode[]) => {
        setProjectState((prev) => {
            if (!prev) return null;
            const data = { ...prev, nodes };
            return new Project(data);
        });
    }, []);

    const updateProjectEdges = useCallback((edges: CustomEdge[]) => {
        setProjectState((prev) => {
            if (!prev) return null;
            const data = { ...prev, edges };
            return new Project(data);
        });
    }, []);

    const renameProject = useCallback(
        async (name: string, description?: string) => { // Added optional description parameter
            if (!project || !user) return;

            setProjectState((prev) => {
                if (!prev) return null;
                const data = { ...prev, name, description: description ?? prev.description }; // Update description if provided
                return new Project(data);
            });

            // Only save if it's an existing project
            if (project.id !== 'unsaved-project') {
                const projectDataToUpdate: Partial<Project> = { name };
                if (description !== undefined) {
                    projectDataToUpdate.description = description;
                }
                
                // It's better to pass only the fields that changed to the service
                // Assuming projectService.updateProject can handle partial updates
                // Or if saveProject is used, ensure it correctly updates or creates
                const updatedProjectInstance = new Project({
                    ...project,
                    ...projectDataToUpdate,
                });

                try {
                    // Assuming projectService.saveProject handles updates if project ID exists
                    await projectService.saveProject(updatedProjectInstance, user.id); 
                    // showNotification('success', 'Project updated successfully'); // Notification moved to RenameDialog
                } catch (error) {
                    console.error('Error updating project details:', error);
                    showNotification('error', 'Failed to update project details in database');
                     // Revert optimistic update if backend save fails
                    setProjectState((prev) => {
                        if (!prev) return null;
                        // Revert to original name and description from before this attempt
                        const revertedData = { ...prev, name: project.name, description: project.description };
                        return new Project(revertedData);
                    });
                }
            }
        },
        [project, user, showNotification]
    );

    // Load project based on URL or start empty
    const { projectId: routeProjectId } = useParams();

    // This effect will run when auth state changes and will initialize the project
    useEffect(() => {
        // Skip if already initialized or auth is still loading
        if (isInitialized || !isAuthChecked) {
            return;
        }

        const initializeProject = async () => {

            setIsInitialized(true);
            try {
                const urlParams = new URLSearchParams(location.search);
                const projectId = routeProjectId || urlParams.get('project');
                const previewToken = urlParams.get('preview');
                const savedProjectId = loadProjectId();

                if (projectId) {
                    if (previewToken) {
                        await loadProject(projectId, true);
                    } else if (user) {
                        // If user is authenticated, load normally
                        await loadProject(projectId);
                    } else {
                        try {
                            await loadProject(projectId);
                        } catch (error) {
                            console.error('Failed to load project with public access:', error);
                            setError(new Error('You must be logged in or have a valid preview link to view this project'));
                            showNotification('error', 'You must be logged in or have a valid preview link to view this project.');
                        }
                    }
                } else if (savedProjectId && user && token) {
                    // If no URL project ID but we have a saved project ID and user is authenticated with a token
                    try {
                        await loadProject(savedProjectId);
                    } catch (error) {
                        console.error('Failed to load saved project:', error);
                        
                        // Clear the saved project ID since we couldn't load it
                        // This could be due to various reasons:
                        // - Project was deleted
                        // - User lost access to the project
                        // - Authentication issues (401 errors)
                        saveProjectId(null);
                        
                        // Check if there's persisted state in the store
                        const storeState = useStore.getState();
                        const hasPersistedNodes = storeState.nodes.length > 0;
                        
                        if (hasPersistedNodes) {
                            // Create a project from the persisted state
                            const projectFromPersistedState = new Project({
                                id: 'unsaved-project',
                                name: 'Untitled Project',
                                nodes: storeState.nodes,
                                edges: storeState.edges,
                                owner_id: user.id,
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString(),
                                shared_with: [],
                                is_locked: false,
                                is_archived: false,
                                version: 1,
                                user_id: user.id,
                                settings: {
                                    preview: null,
                                },
                            });
                            
                            // Update project context but don't overwrite the store
                            setProjectState(projectFromPersistedState);
                        }
                    }
                } else if (savedProjectId && (!user || !token)) {
                    // We have a saved project ID but no authenticated user or no token
                    // We'll use the persisted flow state but won't try to load the project
                    // The project ID will remain in localStorage for when the user logs in
                    
                    // Check if there's persisted state in the store
                    const storeState = useStore.getState();
                    const hasPersistedNodes = storeState.nodes.length > 0;
                    
                    if (hasPersistedNodes) {
                        // Create a project from the persisted state
                        const projectFromPersistedState = new Project({
                            id: 'unsaved-project',
                            name: 'Untitled Project',
                            nodes: storeState.nodes,
                            edges: storeState.edges,
                            owner_id: user?.id || 'anonymous',
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            shared_with: [],
                            is_locked: false,
                            is_archived: false,
                            version: 1,
                            user_id: user?.id || 'anonymous',
                            settings: {
                                preview: null,
                            },
                        });
                        
                        // Update project context but don't overwrite the store
                        setProjectState(projectFromPersistedState);
                    }
                } else {
                    // No project ID in URL or localStorage, or we have a project ID but no user and no persisted state
                    // Check if there's persisted state in the store
                    const storeState = useStore.getState();
                    const hasPersistedNodes = storeState.nodes.length > 0;
                    
                    if (hasPersistedNodes) {
                        // Create a project from the persisted state
                        const projectFromPersistedState = new Project({
                            id: 'unsaved-project',
                            name: 'Untitled Project',
                            nodes: storeState.nodes,
                            edges: storeState.edges,
                            owner_id: user?.id || 'anonymous',
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            shared_with: [],
                            is_locked: false,
                            is_archived: false,
                            version: 1,
                            user_id: user?.id || 'anonymous',
                            settings: {
                                preview: null,
                            },
                        });
                        
                        // Update project context but don't overwrite the store
                        setProjectState(projectFromPersistedState);
                    } else if (user) {
                        // No persisted state and user is logged in, create an empty project
                        const emptyProject = new Project({
                            id: 'unsaved-project',
                            name: 'Untitled Project',
                            nodes: [],
                            edges: [],
                            owner_id: user.id,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            shared_with: [],
                            is_locked: false,
                            is_archived: false,
                            version: 1,
                            user_id: user.id,
                            settings: {
                                preview: null,
                            },
                        });

                        // Update both project context and store
                        setProjectState(emptyProject);
                        useStore.getState().setProject(emptyProject);
                    } else {
                        // No project ID and no user, show error
                        setError(new Error('You must be logged in to create a new project'));
                        showNotification('error', 'You must be logged in to create a new project.');
                    }
                }
            } catch (error) {
                console.error('Failed to initialize project:', error);
                setError(error as Error);
                showNotification('error', 'Failed to initialize project. Please try refreshing the page.');
            }
        };

        initializeProject();
    }, [isInitialized, isAuthChecked, authLoading, user, token, showNotification, location.search, loadProject, routeProjectId]);

    // Fit view whenever project changes and nodes are available
    useEffect(() => {
        if (project && fitView) {
            // Use requestAnimationFrame to ensure nodes are rendered
            const timeoutId = requestAnimationFrame(() => {
                fitView({ padding: 0.2, includeHiddenNodes: false });
            });
            return () => cancelAnimationFrame(timeoutId);
        }
    }, [project, fitView]);

    const loadFromTemplate = useCallback(
        async (templateId: string) => {
            setIsLoading(true);
            try {
                const template = await templateService.getTemplate(templateId);

                const nodes = template.nodes as CustomNode[];
                const edges = template.edges as CustomEdge[];

                // Create new unsaved project from template
                const newProject = new Project({
                    id: 'unsaved-project',
                    name: 'Untitled Project',
                    nodes,
                    edges,
                    owner_id: user?.id || 'anonymous',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    shared_with: [],
                    is_locked: false,
                    is_archived: false,
                    version: 1,
                    user_id: user?.id || 'anonymous',
                    settings: {
                        preview: null,
                    },
                });

                // Update both project context and store
                setProjectState(newProject);
                useStore.getState().setProject(newProject);
                showNotification('success', `Template "${template.name}" loaded successfully`);
            } catch (err) {
                setError(err as Error);
                showNotification('error', 'Failed to load template');
            } finally {
                setIsLoading(false);
            }
        },
        [user, showNotification]
    );


    const shareProject = useCallback(
        async (projectId: string, email: string): Promise<Project | { message: string } | undefined> => {
            if (!project || !user) {
                showNotification('error', 'You must be logged in to share a project');
                return { message: 'User not authenticated' };
            }
            setIsLoading(true);
            try {
                // Ensure the project is saved before sharing
                if (project.id === 'unsaved-project') {
                    // Get current store state
                    const storeState = useStore.getState();

                    // Deselect nodes and edges before processing for saving
                    const deselectedNodes = storeState.nodes.map(node => ({ ...node, selected: false }));
                    const deselectedEdges = storeState.edges.map(edge => ({ ...edge, selected: false }));

                    // Process nodes and edges before saving (using deselected versions)
                    const nodesToSave = deselectedNodes.map((node) => ({
                        ...node,
                        data: {
                            ...node.data,
                            onClose: undefined,
                            onDelete: undefined,
                            onUpdate: undefined,
                        },
                    }));

                    // Process edges to ensure all relevant properties are saved (using deselected versions)
                    const edgesToSave = deselectedEdges.map((edge) => {
                        // Create a copy to modify
                        let edgeToSave = { ...edge };
                        const edgeData = edgeToSave.data || {};
                        const edgeLabel = edgeData.label || edgeToSave.label || '';

                        // Always store label in data.label for all edge types
                        edgeToSave.data = { ...edgeData, label: edgeLabel };
                        
                        // Keep top-level label for backward compatibility
                        edgeToSave.label = edgeLabel;

                        // Return the modified edge object
                        return {
                            ...edgeToSave,
                            id: edgeToSave.id,
                            source: edgeToSave.source,
                            target: edgeToSave.target,
                            sourceHandle: edge.sourceHandle || null,
                            targetHandle: edge.targetHandle || null,
                            type: edge.type || 'editable-edge',
                            animated: edge.animated || false,
                            style: edge.style,
                            markerStart: edge.markerStart,
                            markerEnd: edge.markerEnd,
                            data: edgeToSave.data,
                        };
                    });
                    
                    const projectToSave = new Project({
                        ...project,
                        nodes: nodesToSave,
                        edges: edgesToSave,
                    });

                    const savedProjectData = await projectService.saveProject(projectToSave, user.id);
                    
                    // Create new project instance with saved data and current nodes/edges
                    const updatedProjectData = new Project({
                        ...savedProjectData,
                        nodes: storeState.nodes, // Keep current nodes in UI
                        edges: storeState.edges, // Keep current edges in UI
                    });

                    setProjectState(updatedProjectData);
                    useStore.getState().setProject(updatedProjectData); // Update store as well
                    
                    // Now that the project is saved, use its ID for sharing
                    projectId = updatedProjectData.id; 
                }

                // If an email is provided, share with that user
                if (email) {
                    // TODO: projectService.shareProject(projectId, email) reported as non-existent on its type.
                    // Sharing with specific email by direct invitation is temporarily disabled.
                    // Original call was: const result = await projectService.shareProject(projectId, email);
                    // Then, based on result:
                    // if (result && 'message' in result) { showNotification('error', result.message); return result; }
                    // else if (result) { showNotification('success', ...); setProjectState(...); return result as Project; }

                    showNotification('warning', `Sharing project with ${email} by direct invitation is temporarily unavailable. You can still share by link if public access is configured.`);
                    // Return a message indicating the issue, fulfilling the Promise type.
                    return { message: `Direct email invitation for ${email} is currently unavailable.` };
                } else {
                    // If no email, just ensure public access is set for link sharing
                    // This part might be handled by a separate function like `setProjectPublicAccess`
                    // For now, we assume sharing implies making it viewable if no specific email.
                    // Or, this case might be for generating a shareable link without adding collaborators.
                    // The actual link generation might happen in the ShareProjectDialog.
                    // This function's primary role here is to ensure the project is saved if it wasn't.
                    
                    // If we just needed to ensure it's saved and get the project data:
                    const currentSavedProject = await projectService.loadProject(projectId);
                    if (!currentSavedProject) throw new Error("Failed to reload project after potential save.");
                    
                    // Update local state with potentially new shared_with list if backend modified it
                    setProjectState(currentSavedProject);
                    useStore.getState().setProject(currentSavedProject);
                    return currentSavedProject;
                }
            } catch (err) {
                setError(err as Error);
                showNotification('error', 'Failed to share project');
                return { message: (err as Error).message };
            } finally {
                setIsLoading(false);
            }
        },
        [project, user, showNotification]
    );


    const loadProjects = useCallback(async () => {
        if (!user) return; // Don't load if user is not authenticated
        setIsLoading(true);
        try {
            const userProjects = await projectService.getUserProjects();
            setProjects(userProjects);
        } catch (err) {
            setError(err as Error);
            showNotification('error', 'Failed to load projects');
        } finally {
            setIsLoading(false);
        }
    }, [user, showNotification]);

    // Load projects when user is authenticated
    useEffect(() => {
        if (user && isAuthChecked) {
            loadProjects();
        } else if (!user && isAuthChecked) {
            // Clear projects if user logs out
            setProjects([]);
        }
    }, [user, isAuthChecked, loadProjects]);



    const isPreviewMode = useMemo(() => {
        const urlParams = new URLSearchParams(location.search);
        return !!urlParams.get('preview');
    }, [location.search]);


    return (
        <>
            {/* Save Dialog for first save */}
            {showSaveDialog && project && (
                <SaveProjectDialog
                    open={showSaveDialog}
                    onClose={handleSaveDialogClose}
                    onSave={handleSaveWithDetails}
                    initialName={project.name === 'Untitled Project' ? '' : project.name}
                />
            )}
            {/* Confirm Dialog for saving non-owned projects */}
            {showSaveConfirmDialog && project && (
                <ConfirmDialog
                    title="Save Changes"
                    message="You are not the owner of this project. Saving will create a copy under your account. Do you want to continue?"
                    onClose={() => setShowSaveConfirmDialog(false)}
                    onConfirm={async () => {
                        setShowSaveConfirmDialog(false);
                        if (!user || !project) {
                            // user and project are from the ProjectProvider's scope.
                            // showNotification is also available in this scope.
                            console.error('User or project became null before confirming save as copy.');
                            showNotification('error', 'Session or project data issue. Please try again.');
                            return;
                        }
                        // Logic to save as a new project (copy)
                        // This might involve resetting the project ID to 'unsaved-project'
                        // and then calling saveProject which will trigger the SaveProjectDialog
                        const newProjectInstance = new Project({
                            ...project,
                            id: 'unsaved-project', // Mark as new
                            owner_id: user.id,    // Set current user as owner (user is now checked)
                            name: `${project.name} (Copy)`, // Append (Copy)
                            shared_with: [],      // Clear shared_with for the copy
                        });
                        setProjectState(newProjectInstance);
                        useStore.getState().setProject(newProjectInstance); // Update store too
                        setShowSaveDialog(true); // Show dialog to confirm name/desc for the new copy
                    }}
                    confirmText="Save as Copy"
                    cancelText="Cancel"
                />
            )}
            <ProjectContext.Provider
                value={{
                    project,
                    projects,
                    isLoading,
                    error,
                    setProject: setProjectState,
                    updateProjectNodes,
                    updateProjectEdges,
                    loadProject,
                    loadProjects,
                    saveProject,
                    renameProject,
                    shareProject,
                    loadFromTemplate,
                    isPreviewMode,
                }}
            >
                {children}
            </ProjectContext.Provider>
        </>
    );
}
